import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API } from '../constants';
import { kitFunctionOf } from '../lib/kitLogic';
import { logError, isUserCancel } from '../lib/log';
import { KitJar3D } from '../components/KitJar3D';
import { useKitJarImages } from '../hooks/useKitJarImages';
import KitPreviewModal from '../components/KitPreviewModal';

const CARD_SIZES = [54, 64, 74, 64, 54];
const CARD_MOTION = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } };

const KitCard = ({ kit, rank, items, fn, onBuy, onShare, onOpen, t }) => {
  const jars = useKitJarImages(items);
  return (
  <motion.div
    className="bk-card"
    {...CARD_MOTION}
    data-testid={`bk-card-${kit.id}`}
  >
    <button type="button" className="bk-card-main" onClick={() => onOpen(kit)} data-testid={`bk-open-${kit.id}`}>
      <div className="bk-jars" data-testid="bk-card-jars">
        {items.slice(0, 5).map((it, i) => <KitJar3D key={`${it.product_type}-${i}`} src={jars[i]} line={it.line_color} size={CARD_SIZES[i] || 54} />)}
      </div>
      <div className="bk-card-body">
        <div className="bk-card-head">
          <h4 className="bk-card-name" data-testid="bk-card-name">{kit.name}</h4>
          {rank <= 3 && <span className={`bk-rank r${rank}`}>#{rank}</span>}
        </div>
        {fn && <span className="bk-fn text-gradient-brand">{fn}</span>}
        <p className="bk-by">
          {t('beautykit.by')} <strong>{kit.creator_name}</strong> · {kit.purchase_count} {t('beautykit.sold')}
        </p>
        <span className="bk-view">{t('beautykit.view')} →</span>
      </div>
    </button>
    <div className="bk-card-foot">
      <span className="bk-price">€{kit.total_price?.toFixed(2) || '0.00'}</span>
      <div className="bk-foot-actions">
        <button onClick={() => onShare(kit)} className="bk-share" aria-label={t('beautykit.share')} title={t('beautykit.share')} data-testid={`bk-share-${kit.id}`}>
          <Share2 size={17} />
        </button>
        <button onClick={() => onBuy(kit)} className="btn-pill btn-pill-primary bk-buy" data-testid={`bk-buy-${kit.id}`}>
          {t('beautykit.buy')}
        </button>
      </div>
    </div>
  </motion.div>
  );
};

const BeautyKitPage = ({ onCreate, products = [], onBuyKit }) => {
  const { t } = useLanguage();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API}/beautykit?public_only=true`);
        setKits(response.data);
      } catch (error) {
        logError('Error fetching public kits:', error);
        toast.error(t('beautykit.link_error'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resolveItems = (kit) => {
    if (Array.isArray(kit.items) && kit.items.length) return kit.items;
    return (kit.products || [])
      .map((id) => products.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => ({ product_type: p.product_type, line_color: p.line_color, main_ingredient: p.main_ingredient, effect: p.effect || p.benefit }));
  };

  const shareKit = async (kit) => {
    const link = `${window.location.origin}/?kit=${kit.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: kit.name, text: `${kit.name} · BeautyKit`, url: link });
        return;
      } catch (error) {
        if (isUserCancel(error)) return;
        logError('Share failed, falling back to clipboard:', error);
      }
    }
    try { await navigator.clipboard.writeText(link); toast.success(t('beautykit.link_copied')); }
    catch (error) { logError('Clipboard unavailable:', error); toast.message(link); }
  };

  if (loading) {
    return (
      <section className="bk-section flex items-center justify-center">
        <div className="text-white text-xl">{t('beautykit.loading')}</div>
      </section>
    );
  }

  return (
    <section className="bk-section" data-testid="beautykit-page">
      <div className="bk-inner">
        <div className="bk-head">
          <span className="eyebrow">{t('beautykit.eyebrow')}</span>
          <h2 className="section-title">{t('beautykit.title')}</h2>
          <p className="section-sub">{t('beautykit.subtitle')}</p>
        </div>

        {kits.length > 0 ? (
          <div className="bk-grid" data-testid="beautykit-grid">
            {kits.map((kit, i) => {
              const items = resolveItems(kit);
              const fn = items.length ? kitFunctionOf(items) : '';
              const open = () => setPreview({ kit, rank: i + 1, items, fn });
              return <KitCard key={kit.id} kit={kit} rank={i + 1} items={items} fn={fn} onBuy={onBuyKit} onShare={shareKit} onOpen={open} t={t} />;
            })}
          </div>
        ) : (
          <div className="bk-empty" data-testid="beautykit-empty">
            <p>{t('beautykit.no_public_kits')}</p>
            <button onClick={onCreate} data-testid="beautykit-create-first" className="btn-pill btn-pill-primary">
              {t('beautykit.create_first')}
            </button>
          </div>
        )}
      </div>
      {preview && (
        <KitPreviewModal {...preview} onClose={() => setPreview(null)} onBuy={onBuyKit} onShare={shareKit} />
      )}
    </section>
  );
};

export default BeautyKitPage;
